import { FC, useEffect, useMemo, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import LinearProgress from '@material-ui/core/LinearProgress';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { resolveQueueErrorMessage } from '../Initialize/resolveQueueErrorMessage';

import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import type { ExerciseAttempt, ExerciseQueue, ExerciseQueueUuid } from 'api/rest/exerciseQueues/exerciseQueues.types';
import exercisesApi from 'api/rest/exercises/exercises';
import { ExerciseFileTypes } from 'api/rest/exercises/exercises.types';
import type { ExerciseAnswer } from 'api/rest/exercises/exercises.types';
import { ReactComponent as TablesIcon } from 'assets/icons/general/tables_white.svg';
import SubjectIcon from 'components/SubjectIcon/SubjectIcon';
import QUERY from 'constants/queryKeys';
import { useTablesContext } from 'context/TablesContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import type { Subject } from 'types/Subject';
import type { Uuid } from 'types/Uuid';

import OnGoLayout from '../OnGoLayout';
import { ON_GO_COLORS } from '../onGoColors';
import { formatAnswerLabels, resolveCorrectAnswers } from './resolveCorrectAnswers';

const TABLES_SUBJECTS = ['CH', 'B', 'FI', 'M', 'MR', 'GE'];

/** Per-question flow: pick/skip, then review feedback before advancing. */
export type ExercisePhase = 'question' | 'result';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
  },
  titleGroup: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
  progressTrack: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    overflow: 'hidden',
    marginBottom: theme.spacing(2.5),
  },
  progressBarAnimated: {
    transition: 'transform 420ms ease',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    flexShrink: 0,
  },
  abortButton: {
    textTransform: 'none',
    color: theme.palette.text.secondary,
  },
  tablesButton: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fill: theme.palette.common.white,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
    },
    '& svg': {
      width: 20,
      height: 20,
    },
    '&$disabled': {
      backgroundColor: theme.palette.grey[300],
      borderColor: theme.palette.grey[300],
      fill: theme.palette.common.white,
      color: theme.palette.common.white,
    },
  },
  disabled: {},
  loader: {
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageSection: {
    marginBottom: theme.spacing(2.5),
  },
  exerciseImage: {
    display: 'block',
    width: '100%',
    margin: theme.spacing(1, 0),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
  },
  choicesSection: {
    display: 'grid',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  choice: {
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 120ms ease, background-color 120ms ease',
  },
  selectedChoice: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  correctChoice: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
  },
  incorrectChoice: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
  },
  mutedChoice: {
    opacity: 0.7,
  },
  result: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  correctResult: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
    color: ON_GO_COLORS.correct,
  },
  incorrectResult: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
    color: ON_GO_COLORS.incorrect,
  },
  resultHeading: {
    fontWeight: 700,
  },
  resultText: {
    color: theme.palette.text.primary,
  },
  explanationButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  explanationButtonCorrect: {
    color: ON_GO_COLORS.correct,
    borderColor: ON_GO_COLORS.correct,
    '&:hover': {
      borderColor: ON_GO_COLORS.correct,
      backgroundColor: ON_GO_COLORS.correctSoft,
    },
  },
  explanationButtonIncorrect: {
    color: ON_GO_COLORS.incorrect,
    borderColor: ON_GO_COLORS.incorrect,
    '&:hover': {
      borderColor: ON_GO_COLORS.incorrect,
      backgroundColor: ON_GO_COLORS.incorrectSoft,
    },
  },
  footer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
  },
  footerResult: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1.5),
  },
  errorSnackbar: {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.error.contrastText,
  },
}));

const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';

type AnswerResult = {
  attempt: ExerciseAttempt;
  nextQueue: ExerciseQueue;
};

type OnGoExerciseProps = {
  queueId: ExerciseQueueUuid;
  subject: Subject;
};

/** Aligns with `TablesContext` provider value (JSX context has no TypeScript shape). */
type TablesContextApi = {
  openCustomImages: (images: string[]) => void;
  openTables: () => void;
};

const OnGoExercise: FC<OnGoExerciseProps> = ({ queueId, subject }) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(null);
  const [answerResult, setAnswerResult] = useState<AnswerResult | null>(null);
  const [isOfficialAnswerVisible, setIsOfficialAnswerVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const tablesCtx = useTablesContext() as TablesContextApi | null;
  const { logEvent } = useUserLogContext();

  const phase: ExercisePhase = answerResult ? 'result' : 'question';

  const { data: queue, isLoading: isQueueLoading } = useQuery(exerciseQueuesApiKeys.detail(queueId), exerciseQueuesApi.getByUuid(queueId), {
    enabled: !!queueId,
  });

  const currentExerciseUuid = queue?.currentItem?.exerciseUuid;

  const { data: exercise, isLoading: isExerciseLoading } = useQuery(
    [QUERY.GET_SINGLE_EXERCISE, currentExerciseUuid],
    exercisesApi.getSingle(currentExerciseUuid as Uuid),
    {
      enabled: !!currentExerciseUuid,
      refetchOnWindowFocus: false,
    },
  );

  const answerMutation = useMutation(exerciseQueuesApi.answer(queueId), {
    onSuccess: ({ attempt, queue: nextQueue }) => {
      setAnswerResult({ attempt, nextQueue });
      logEvent('on-go-answer', {
        subjectPk: subject.pk,
        subjectCode: subject.subjectCode,
        queueUuid: queueId,
        correct: attempt.correct,
        exerciseIndex: exercise?.index,
      });
    },
    onError: error => {
      setErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  const skipMutation = useMutation(exerciseQueuesApi.skip(queueId), {
    onSuccess: nextQueue => {
      queryClient.setQueryData(exerciseQueuesApiKeys.detail(queueId), nextQueue);
      queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), nextQueue);
      logEvent('on-go-skip', {
        subjectPk: subject.pk,
        subjectCode: subject.subjectCode,
        queueUuid: queueId,
      });
    },
    onError: error => {
      setErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  const closeMutation = useMutation(exerciseQueuesApi.close(queueId), {
    onSuccess: () => {
      queryClient.invalidateQueries(exerciseQueuesApiKeys.active(subject.pk));
    },
    onError: error => {
      setErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  useEffect(() => {
    if (!currentExerciseUuid) {
      return;
    }
    setSelectedAnswerId(null);
    setAnswerResult(null);
    setIsOfficialAnswerVisible(false);
  }, [currentExerciseUuid]);

  const selectedAnswer = useMemo(
    () => exercise?.answers.find(answer => answer.id === selectedAnswerId),
    [exercise?.answers, selectedAnswerId],
  );

  const correctAnswers = useMemo(() => {
    if (!exercise?.answers || !answerResult) {
      return [];
    }
    return resolveCorrectAnswers(exercise.answers, answerResult.attempt, selectedAnswer);
  }, [exercise?.answers, answerResult, selectedAnswer]);

  const correctAnswerIdSet = useMemo(() => new Set(correctAnswers.map(answer => answer.id)), [correctAnswers]);

  const isSubmitting = answerMutation.isLoading || skipMutation.isLoading || closeMutation.isLoading;
  const isContentLoading = isQueueLoading || isExerciseLoading;
  const questionImages = exercise?.files[ExerciseFileTypes.question] || [];
  const officialAnswerImages = exercise?.files[ExerciseFileTypes.answer] || [];
  const attachmentImages = exercise?.files[ExerciseFileTypes.attachment] || [];
  const canOpenTables = subject.subjectCode === 'GE' ? attachmentImages.length > 0 : TABLES_SUBJECTS.includes(subject.subjectCode);
  const progressQueue = answerResult?.nextQueue ?? queue;
  const progressPercent = progressQueue?.progress.total
    ? Math.min(100, Math.max(0, (progressQueue.progress.answered / progressQueue.progress.total) * 100))
    : 0;
  // Title tracks answered progress (+1 while answering); skip advances the item but not `answered`.
  const exercisePosition =
    progressQueue?.progress.total != null
      ? phase === 'result'
        ? progressQueue.progress.answered
        : progressQueue.progress.answered + 1
      : null;
  const exerciseTitle =
    exercisePosition != null && progressQueue?.progress.total
      ? `Zadanie ${exercisePosition} z ${progressQueue.progress.total}`
      : 'Zadanie';

  const onCheckAnswer = () => {
    if (phase !== 'question' || selectedAnswerId == null) {
      return;
    }
    answerMutation.mutate({ answer_id: selectedAnswerId });
  };

  const onNextQuestion = () => {
    if (!answerResult) {
      return;
    }
    queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), answerResult.nextQueue);
    queryClient.setQueryData(exerciseQueuesApiKeys.detail(queueId), answerResult.nextQueue);
    setAnswerResult(null);
    setSelectedAnswerId(null);
  };

  const onSkip = () => {
    if (phase === 'question' && !isSubmitting) {
      skipMutation.mutate();
    }
  };

  const onTablesClick = () => {
    if (subject.subjectCode === 'GE') {
      tablesCtx?.openCustomImages(attachmentImages);
      return;
    }
    tablesCtx?.openTables();
  };

  const onAbort = () => {
    if (isSubmitting) {
      return;
    }
    const confirmed = window.confirm('Postęp w tej kolejce zostanie utracony. Czy na pewno chcesz przerwać sesję?');
    if (!confirmed) {
      return;
    }
    logEvent('on-go-abort', {
      subjectPk: subject.pk,
      subjectCode: subject.subjectCode,
      queueUuid: queueId,
      progressAnswered: queue?.progress.answered,
    });
    closeMutation.mutate();
  };

  const getChoiceClassName = (answer: ExerciseAnswer) => {
    const isSelected = answer.id === selectedAnswerId;
    const isCorrectOption = correctAnswerIdSet.has(answer.id);

    if (phase === 'question') {
      return clsx(classes.choice, isSelected && classes.selectedChoice);
    }

    if (answerResult) {
      if (isCorrectOption) {
        return clsx(classes.choice, classes.correctChoice);
      }
      if (isSelected && !answerResult.attempt.correct) {
        return clsx(classes.choice, classes.incorrectChoice);
      }
      return clsx(classes.choice, classes.mutedChoice);
    }

    return classes.choice;
  };

  const correctAnswersLabel = correctAnswers.length > 0 ? formatAnswerLabels(correctAnswers) : null;
  const correctAnswersHeading = correctAnswers.length > 1 ? 'Poprawne odpowiedzi' : 'Poprawna odpowiedź';
  const nextButtonLabel = answerResult?.nextQueue.completedAt ? 'Podsumowanie' : 'Następne';

  return (
    <OnGoLayout
      footer={
        phase === 'question' ? (
          <Box className={classes.footer}>
            <Button disabled={isSubmitting} onClick={onSkip} variant='outlined'>
              Pomiń
            </Button>
            <Button color='primary' disabled={isSubmitting || selectedAnswerId == null} onClick={onCheckAnswer} variant='contained'>
              Dalej
            </Button>
          </Box>
        ) : (
          <Box className={classes.footerResult}>
            <Button color='primary' disabled={isSubmitting} fullWidth onClick={onNextQuestion} variant='contained'>
              {nextButtonLabel}
            </Button>
          </Box>
        )
      }
    >
      <Box className={classes.header}>
        <Box className={classes.titleGroup}>
          <SubjectIcon iconName={subject.subjectCode} size='s' />
          <Box minWidth={0}>
            <Typography className={classes.title} component='h1' noWrap variant='h6'>
              {exerciseTitle}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerActions}>
          <Button
            aria-label='Przerwij sesję'
            className={classes.abortButton}
            disabled={isSubmitting}
            onClick={onAbort}
            size='small'
            variant='text'
          >
            Przerwij
          </Button>
          <ButtonBase
            aria-label={subject.subjectCode === 'GE' ? 'Otwórz pomoce' : 'Otwórz tablice'}
            className={clsx(classes.tablesButton, !canOpenTables && classes.disabled)}
            disabled={!canOpenTables}
            onClick={onTablesClick}
          >
            <TablesIcon />
          </ButtonBase>
        </Box>
      </Box>
      {progressQueue ? (
        <LinearProgress
          aria-label='Postęp sesji'
          className={classes.progressTrack}
          classes={{ bar: classes.progressBarAnimated }}
          color='primary'
          value={progressPercent}
          variant='determinate'
        />
      ) : null}

      {isContentLoading ? (
        <Box className={classes.loader}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          <Box aria-label='Treść zadania' className={classes.imageSection} component='section'>
            {questionImages.length ? (
              questionImages.map(src => <img key={src} alt='' className={classes.exerciseImage} src={`${mediaUrl}${src}`} />)
            ) : (
              <Typography align='center' color='textSecondary'>
                Brak treści zadania do wyświetlenia.
              </Typography>
            )}
          </Box>

          {phase === 'result' && answerResult ? (
            <Box
              aria-live='polite'
              className={clsx(classes.result, answerResult.attempt.correct ? classes.correctResult : classes.incorrectResult)}
              role='status'
            >
              <Typography className={classes.resultHeading} variant='subtitle1'>
                {answerResult.attempt.correct ? 'Poprawna odpowiedź' : 'Niepoprawna odpowiedź'}
              </Typography>
              <Typography className={classes.resultText} variant='body2'>
                Twoja odpowiedź: {selectedAnswer?.label ?? answerResult.attempt.answer}
              </Typography>
              {!answerResult.attempt.correct && correctAnswersLabel ? (
                <Typography className={classes.resultText} variant='body2'>
                  {correctAnswersHeading}: {correctAnswersLabel}
                </Typography>
              ) : null}
              {officialAnswerImages.length > 0 ? (
                <Button
                  className={clsx(
                    classes.explanationButton,
                    answerResult.attempt.correct ? classes.explanationButtonCorrect : classes.explanationButtonIncorrect,
                  )}
                  onClick={() => setIsOfficialAnswerVisible(prev => !prev)}
                  size='small'
                  variant='outlined'
                >
                  {isOfficialAnswerVisible ? 'Ukryj wyjaśnienie' : 'Pokaż wyjaśnienie'}
                </Button>
              ) : null}
              {isOfficialAnswerVisible ? (
                <Box aria-label='Oficjalna odpowiedź' component='section' mt={1}>
                  {officialAnswerImages.map(src => (
                    <img key={src} alt='' className={classes.exerciseImage} src={`${mediaUrl}${src}`} />
                  ))}
                </Box>
              ) : null}
            </Box>
          ) : null}

          <Box className={classes.choicesSection} component='section'>
            <Typography className={classes.sectionTitle} component='h2' variant='subtitle1'>
              {phase === 'question' ? 'Wybierz odpowiedź' : 'Odpowiedzi'}
            </Typography>
            {exercise?.answers.length ? (
              exercise.answers.map(answer => (
                <ButtonBase
                  key={answer.id}
                  aria-pressed={phase === 'question' ? answer.id === selectedAnswerId : undefined}
                  className={getChoiceClassName(answer)}
                  disabled={phase === 'result' || isSubmitting}
                  onClick={() => {
                    if (phase === 'question') {
                      setSelectedAnswerId(answer.id);
                    }
                  }}
                >
                  <Typography>{answer.label}</Typography>
                </ButtonBase>
              ))
            ) : (
              <Typography color='textSecondary' variant='body2'>
                To zadanie nie ma jeszcze skonfigurowanych odpowiedzi.
              </Typography>
            )}
          </Box>
        </>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        open={!!errorMessage}
      >
        <SnackbarContent className={classes.errorSnackbar} message={errorMessage ?? ''} role='alert' />
      </Snackbar>
    </OnGoLayout>
  );
};

export default OnGoExercise;
